import { useTranslation } from 'react-i18next';
import { ChangeEvent, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { Select } from '../../../ui/Select';
import { selectSearchFormCurrentSortType } from '../../../../store/selectors';
import { SORTING_VARIANTS } from '../../../../utils/helpers/constants';
import {
  SortingContainer,
  popoverFilterStyles,
  SelectedValueContainer
} from './styled';
import useScreenSize from '../../../../hooks/useScreenSize';
import SortIcon from '../../../../public/static/images/new_search/icons/icon-sort.svg';
import Icon from '../../../Icon';
import { SelectHorizontalAlignmentEnum } from '../../../ui/Select/types';
import { NewRadioButton } from '../../../NewRadioButton';
import { generateSortingHrefAndAs } from '../../../../utils/helpers/generateSortingHrefAndAs';
import { setSearchSort } from '../../../../actions';
import { FilterFieldProps, SelectRefType } from '../types';
import { getDataForTransformationsQuery } from '../../../../api/queries';
import { GetDataForTransformationsQuery } from '../../../../graphql/graphql';

const DATE_SORTING = '1';

export const SortingFilter = ({ submitOnChange = false }: FilterFieldProps) => {
  const { t } = useTranslation();
  const { isSmall } = useScreenSize();
  const router = useRouter();
  const dispatch = useDispatch();
  const currentSort = useSelector(selectSearchFormCurrentSortType);
  const selectRef = useRef<SelectRefType>(null);
  const currentSortValue = currentSort || DATE_SORTING;

  const { data } = useQuery<GetDataForTransformationsQuery>(
    getDataForTransformationsQuery,
    { fetchPolicy: 'cache-only' }
  );

  const value = SORTING_VARIANTS.find(
    variant => variant.id === currentSortValue
  );

  const icon = (
    <Icon IconComponent={SortIcon} width={20} height={20} indent={false} />
  );

  const selectedValue = (
    <SelectedValueContainer>
      {icon} {t(value?.name || '')}
    </SelectedValueContainer>
  );

  const sortingPlaceholder = `${t('search.buttons.sort')}`;

  const onChangeRadioHandler = (event?: ChangeEvent<HTMLInputElement>) => {
    if (!event) return;

    const { search, ...queryObj } = router.query;

    const { href, as } = generateSortingHrefAndAs({
      queryObj,
      search,
      sortValue: event.target.value,
      sortData: data.sort,
      pathname: router.pathname
    });

    dispatch(setSearchSort(event.target.value));
    router.push(href, as);

    if (submitOnChange && selectRef.current) {
      selectRef.current.closePopover();
    }
  };

  return (
    <Select
      ref={selectRef}
      placeholder={sortingPlaceholder}
      value={selectedValue}
      cypress="new-search-sorting-modal"
      stat="new-search-sorting-modal"
      horizontalAlign={SelectHorizontalAlignmentEnum.Right}
      width={251}
      modalTitle={t('search.buttons.sort')}
      showIcon={!isSmall}
      styles={popoverFilterStyles}
      modalCypress="filter-modal"
    >
      <SortingContainer>
        {SORTING_VARIANTS.map(sorting => (
          <NewRadioButton
            data={{ ...sorting, name: t(sorting.name) }}
            key={sorting.id}
            cypress="sorting"
            stat={sorting.stat}
            name="sorting"
            currentValue={currentSort || '1'}
            onChangeHandler={onChangeRadioHandler}
          />
        ))}
      </SortingContainer>
    </Select>
  );
};
